// ==============================|| PUSH API ||============================== //
// Session-cookie based, same pattern as api/registration.js. 2026-08-22 -
// fixes the reported bug: the old "Povolit push notifikace" toggle
// (UserSettingsAccordion.jsx) read raw browser Notification.permission,
// which is shared by every account on a shared/shift PC - a new user saw
// "enabled" just because a colleague had granted it earlier. subscribeToPush()/
// isSubscribedForThisAccount() below are real, per-account, backend-verified
// state instead.

import { notifySessionExpired } from 'utils/sessionExpired';

async function fetchCsrfToken() {
  const res = await fetch('/auth/csrf-token', { credentials: 'include' });
  const body = await res.json();
  return body?.data?.csrf_token;
}

async function apiGet(path) {
  const res = await fetch(path, { credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    if (res.status === 401) notifySessionExpired();
    throw new Error(body.message || 'Požadavek se nezdařil.');
  }
  return body.data;
}

async function apiPost(path, payload) {
  const csrfToken = await fetchCsrfToken();
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(payload)
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    if (res.status === 401) notifySessionExpired();
    const error = new Error(body.message || 'Požadavek se nezdařil.');
    error.code = body.code;
    throw error;
  }
  return body.data;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export const isPushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';

export const getVapidPublicKey = () => apiGet('/push/vapid-public-key');

// Real per-account state (backend push_subscriptions row), NOT
// Notification.permission - see this file's header comment.
export const getSubscriptionStatus = () => apiGet('/push/status');

async function registerSubscription(subscription) {
  return apiPost('/push/subscribe', subscription.toJSON());
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifikace nejsou v tomto prohlížeči podporované.');
  }

  // Prohlížeč jednou zamítnuté oprávnění už znovu sám nenabídne -
  // Notification.requestPermission() by se vyřešilo rovnou na 'denied' bez
  // jakékoliv viditelné výzvy, což vypadá jako by klik nic neudělal.
  if (Notification.permission === 'denied') {
    throw new Error('Oprávnění je v prohlížeči trvale zablokované - povolte notifikace pro tuto stránku ručně v nastavení prohlížeče.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Oprávnění pro notifikace nebylo uděleno.');
  }

  const registration = await navigator.serviceWorker.ready;
  const { public_key: vapidPublicKey } = await getVapidPublicKey();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  }

  await registerSubscription(subscription);
  return subscription;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  // Vždy smaže VŠECHNY odběry účtu, ne jen endpoint, který si tenhle
  // prohlížeč zrovna pamatuje (2026-08-23, bug nahlášen na mobilu - "Vypnout"
  // tvářil jako úspěch, ale server pořád evidoval aktivní zařízení). Mobilní
  // prohlížeče (hlavně Android/Chrome) občas na pozadí obmění push endpoint
  // ("endpoint rotation") - server pak smazal 0 řádků (DELETE bez shody
  // nehlásí chybu), takže UI ukázalo "vypnuto", ale reálně aktivní záznam
  // zůstal osiřelý. Stav v appce (getSubscriptionStatus/hasActiveForUser) je
  // navíc od začátku vždy za CELÝ účet, ne za jednotlivé zařízení, takže
  // tenhle úklid "všeho najednou" jen sjednocuje akci se zobrazeným stavem -
  // správa jednotlivých zařízení zvlášť je teď admin nástroj "Uvolnění
  // zařízení" (Push.jsx).
  await apiPost('/push/unsubscribe', {});
  if (subscription) {
    await subscription.unsubscribe();
  }
}

export const getUnreadNotifications = () => apiGet('/push/notifications/unread');

export const markNotificationRead = (id) => apiPost(`/push/notifications/${id}/read`, {});

// Groups the caller may target a send at (PushAuthorizer::sendableGroupIds() -
// superadmin sees all, others see their administered subtree + any group
// they hold a direct >= editor push grant in) - drives the "Push zprávy"
// admin page's picker so it never offers a group that would then 403.
export const getSendableGroups = () => apiGet('/push/sendable-groups');

// target: { group_id } for a whole group, or { user_id } for one specific
// person - PushController::send() accepts either. Authorization is always
// re-checked backend-side (PushAuthorizer::canSendToGroup()/canSendToUser()),
// this is just which field to send.
export const sendPush = (target, title, body, url) => apiPost('/push/send', { ...target, title, body, url: url || undefined });

// Admin-only (route-gated system/admin) - lists/releases a user's registered
// devices, e.g. to free one up after an employee leaves and their device
// gets reassigned (see PUSH-409-001 - store() otherwise refuses to reclaim
// an endpoint still tied to a different account).
export const listUserDevices = (userId) => apiGet(`/admin/push/devices?user_id=${userId}`);

export const releaseDevice = (id) => apiPost(`/admin/push/devices/${id}/release`, {});
